import React, { useState } from "react";
import Select from "react-select";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import { getNames } from "country-list";
import {
  Check,
  User,
  Mail,
  Briefcase,
  Building2,
  MapPin,
  Building,
  FileText,
} from "lucide-react";
import HeaderMenu from "../component/Navbar/headermenu";
import CustomFooter from "../component/Footer/customFooter";
import { Helmet } from "react-helmet-async";
import apiClient from "../config/apiClient";
import "./PartnerForm.css";

const countryOptions = Object.entries(getNames()).map(([code, name]) => ({
  value: name,
  label: name,
}));

const businessTypeMap = {
  "Law Firm": "law_firm",
  "Tax Consultancy": "tax_consultancy",
  "SaaS Software Provider": "saas_software_provider",
  "Publishing House": "publishing_house",
  "IT Service Provider": "it_service_provider",
  "Educational Services": "educational_services",
  "Chartered Accountant": "chartered_accountant",
  Lawyer: "lawyer",
  Other: "other",
};

const clientsMap = {
  "Chartered Accountants": "chartered_accountants",
  "Lawyers/ Advocates": "lawyers_advocates",
  "Tax Consultants": "tax_consultants",
  "Government/PSU": "government_psu",
  "SMEs/MSMEs": "smes_msmes",
  "Educational Institutions": "educational_institutions",
  "Judicial Bodies": "judicial_bodies",
  Corporates: "corporates",
  Other: "other",
};

const marketingMap = {
  "Email Marketing": "email_marketing",
  "Social Media Campaigns": "social_media_campaigns",
  "Events/Seminars": "events_seminars",
  Other: "other",
};

const businessTypes = [
  { value: "Law Firm", label: "Law Firm" },
  { value: "Tax Consultancy", label: "Tax Consultancy" },
  { value: "SaaS Software Provider", label: "SaaS Software Provider" },
  { value: "Publishing House", label: "Publishing House" },
  { value: "IT Service Provider", label: "IT Service Provider" },
  { value: "Educational Services", label: "Educational Services" },
  { value: "Chartered Accountant", label: "Chartered Accountant" },
  { value: "Lawyer", label: "Lawyer" },
  { value: "Other", label: "Other" },
];

const clientsOptions = [
  { value: "Chartered Accountants", label: "Chartered Accountants" },
  { value: "Lawyers/ Advocates", label: "Lawyers/ Advocates" },
  { value: "Tax Consultants", label: "Tax Consultants" },
  { value: "Government/PSU", label: "Government/PSU" },
  { value: "SMEs/MSMEs", label: "SMEs/MSMEs" },
  { value: "Educational Institutions", label: "Educational Institutions" },
  { value: "Judicial Bodies", label: "Judicial Bodies" },
  { value: "Corporates", label: "Corporates" },
  { value: "Other", label: "Other" },
];

const marketingOptions = [
  { value: "Email Marketing", label: "Email Marketing" },
  { value: "Social Media Campaigns", label: "Social Media Campaigns" },
  { value: "Events/Seminars", label: "Events/Seminars" },
  { value: "Other", label: "Other" },
];

const yearsOptions = [
  { value: "Less than 1 year", label: "Less than 1 year" },
  { value: "1-3 years", label: "1-3 years" },
  { value: "3-5 years", label: "3-5 years" },
  { value: "5+ years", label: "5+ years" },
];

const productOptions = [
  { value: "Expert", label: "Expert" },
  { value: "Intermediate", label: "Intermediate" },
  { value: "Beginner", label: "Beginner" },
  { value: "Not Familiar", label: "Not Familiar" },
];

const yesNoOptions = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

const STEPS = ["Business", "Contact", "Experience"];

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PartnerForm = () => {
  const [step, setStep] = useState(1);
  const [showPartnerSubmitted, setShowPartnerSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialFormData = {
    businessName: "",
    businessType: [],
    businessTypeOther: "",
    yearsOperation: "",
    applicantName: "",
    applicantTitle: "",
    phone: "",
    email: "",
    country: "",
    address: "",
    city: "",
    state: "",
    existingClients: [],
    existingClientsOther: "",
    soldSaaSBefore: "",
    familiarity: "",
    hasSalesTeam: "",
    currentMarketing: [],
    currentMarketingOther: "",
  };
  const [formData, setFormData] = useState(initialFormData);

  const clearError = (field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const validateStep = (targetStep) => {
    const nextErrors = {};

    if (targetStep === 1) {
      if (!formData.businessName.trim())
        nextErrors.businessName = "Business name is required.";
      if (!formData.businessType.length)
        nextErrors.businessType = "Select at least one business type.";
      if (!formData.yearsOperation)
        nextErrors.yearsOperation = "Select years in operation.";
      if (!formData.applicantName.trim())
        nextErrors.applicantName = "Applicant name is required.";
      if (!formData.applicantTitle.trim())
        nextErrors.applicantTitle = "Position/title is required.";
    }

    if (targetStep === 2) {
      if (!formData.phone.trim()) nextErrors.phone = "Phone number is required.";
      if (!formData.email.trim()) {
        nextErrors.email = "Email is required.";
      } else if (!emailPattern.test(formData.email.trim())) {
        nextErrors.email = "Enter a valid email address.";
      }
      if (!formData.country) nextErrors.country = "Select a country.";
      if (!formData.city.trim()) nextErrors.city = "City is required.";
      if (!formData.address.trim())
        nextErrors.address = "Business address is required.";
    }

    if (targetStep === 3) {
      if (!formData.existingClients.length)
        nextErrors.existingClients = "Select at least one client type.";
      if (!formData.soldSaaSBefore)
        nextErrors.soldSaaSBefore = "This field is required.";
      if (!formData.familiarity)
        nextErrors.familiarity = "This field is required.";
      if (!formData.hasSalesTeam)
        nextErrors.hasSalesTeam = "This field is required.";
      if (!formData.currentMarketing.length)
        nextErrors.currentMarketing = "Select at least one activity.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setSubmitError("");
      setStep((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleBack = () => {
    setErrors({});
    setStep((prev) => prev - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toBool = (v) => (v === "Yes" ? true : v === "No" ? false : null);

  const yearsMap = {
    "Less than 1 year": "less_than_1_year",
    "1-3 years": "1_3_years",
    "3-5 years": "3_5_years",
    "5+ years": "5_plus_years",
  };

  const familiarityMap = {
    Expert: "expert",
    Intermediate: "intermediate",
    Beginner: "beginner",
    "Not Familiar": "not_familiar",
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (!validateStep(3)) return;

    const apiData = {
      business_name: formData.businessName,
      business_types: formData.businessType.map(
        (item) => businessTypeMap[item.value]
      ),
      years_in_operation: yearsMap[formData.yearsOperation] ?? null,
      applicant_name: formData.applicantName,
      position_title: formData.applicantTitle,
      phone_e164: formData.phone,
      email: formData.email,
      country: formData.country,
      city: formData.city,
      state: formData.state,
      business_address: formData.address,
      existing_clients: formData.existingClients.map(
        (item) => clientsMap[item.value]
      ),
      other_clients_text: formData.existingClientsOther || "",
      previously_sold: toBool(formData.soldSaaSBefore),
      familiarity_level: familiarityMap[formData.familiarity] ?? null,
      has_sales_marketing_team: toBool(formData.hasSalesTeam),
      marketing_activities: formData.currentMarketing.map(
        (item) => marketingMap[item.value]
      ),
      other_marketing_text: formData.currentMarketingOther || "",
    };

    setIsSubmitting(true);
    try {
      const response = await apiClient.post("/partners/apply", apiData);
      if (response === 200 || response.status === 201) {
        setShowPartnerSubmitted(true);
        setStep(0);
      } else {
        setSubmitError(`Submission failed with status ${response.status}.`);
      }
    } catch (error) {
      if (error.response) {
        setSubmitError(
          error.response.data?.message || "Failed to submit the form."
        );
      } else if (error.request) {
        setSubmitError(
          "Network error. Please check your connection and try again."
        );
      } else {
        setSubmitError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isOtherSelected = (array) =>
    array.some((option) => option.value === "Other");

  return (
    <>
      <Helmet>
        <title>Apply For Partners | Simplifyte</title>
        <meta property="og:title" content="Apply For Partners | Simplifyte" />
        <meta
          name="description"
          property="og:description"
          content="Let's grow, together."
        />
        <link rel="canonical" href="https://www.simplifyte.com/partnerform" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta
          name="keywords"
          content="Simplifyte partners, apply for partnership, business collaboration, partner program, become a partner, SaaS partnership"
        />
      </Helmet>
      <div className="PF_main">
        <HeaderMenu />
        <section className="PF_section">
          <div className="PF_container">
            <div className="PF_grid">
              {/* Left / intro */}
              <div>
                <span className="PF_intro_eyebrow">Partner Application</span>
                <h1 className="PF_intro_title">Let's grow, together.</h1>
                <p className="PF_intro_subtitle">
                  Join our mission to simplify how the world works — one team
                  at a time. Tell us about your business and we'll take it
                  from there.
                </p>

                <div className="PF_steps_list">
                  <div className="PF_steps_item">
                    <span className="PF_steps_number">1</span>
                    <span className="PF_steps_text">
                      <strong>Apply Now</strong> — Fill out a quick
                      application to get started.
                    </span>
                  </div>
                  <div className="PF_steps_item">
                    <span className="PF_steps_number">2</span>
                    <span className="PF_steps_text">
                      <strong>We Verify</strong> — Our team carefully reviews
                      your details.
                    </span>
                  </div>
                  <div className="PF_steps_item">
                    <span className="PF_steps_number">3</span>
                    <span className="PF_steps_text">
                      <strong>Get Approved</strong> — Once verified, you're
                      officially a partner.
                    </span>
                  </div>
                  <div className="PF_steps_item">
                    <span className="PF_steps_number">4</span>
                    <span className="PF_steps_text">
                      <strong>Start Growing</strong> — Access your dedicated
                      panel & support to scale.
                    </span>
                  </div>
                </div>

                <p className="PF_intro_footnote">See you on the other side.</p>
              </div>

              {/* Right / form */}
              <div className="PF_card">
                {showPartnerSubmitted ? (
                  <div className="PF_success">
                    <div className="PF_success_icon">
                      <Check size={28} strokeWidth={2.5} />
                    </div>
                    <h3 className="PF_success_title">
                      Partnership begins with trust
                    </h3>
                    <p className="PF_success_text">
                      Thanks for reaching out. Your application has been
                      received, and Team Simplifyte is reviewing it with
                      care. We'll soon be in touch to build something
                      impactful together.
                    </p>
                    <button
                      onClick={() => {
                        setShowPartnerSubmitted(false);
                        setFormData(initialFormData);
                        setErrors({});
                        setStep(1);
                      }}
                      className="PF_button PF_button_primary"
                    >
                      Done
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="PF_progress">
                      {STEPS.map((label, index) => {
                        const stepNum = index + 1;
                        const isActive = step === stepNum;
                        const isDone = step > stepNum;
                        return (
                          <React.Fragment key={label}>
                            <div className="PF_progress_step">
                              <div
                                className={`PF_progress_dot ${
                                  isActive ? "is-active" : ""
                                } ${isDone ? "is-done" : ""}`}
                              >
                                {isDone ? <Check size={16} /> : stepNum}
                              </div>
                              <span
                                className={`PF_progress_label ${
                                  isActive ? "is-active" : ""
                                }`}
                              >
                                {label}
                              </span>
                            </div>
                            {stepNum < STEPS.length && (
                              <div className="PF_progress_line">
                                <div
                                  className={`PF_progress_line_fill ${
                                    isDone ? "is-done" : ""
                                  }`}
                                />
                              </div>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>

                    {submitError && (
                      <div className="PF_banner_error">{submitError}</div>
                    )}

                    <form onSubmit={handleSubmit}>
                      <div className="PF_fields">
                        {step === 1 && (
                          <>
                            <Field
                              label="Business Information"
                              name="businessName"
                              placeholder="Company / Individual name"
                              value={formData.businessName}
                              onChange={handleChange}
                              icon={Building2}
                              error={errors.businessName}
                            />
                            <SelectField
                              label="Type of Business"
                              placeholder="Choose as many as you like"
                              options={businessTypes}
                              value={formData.businessType}
                              onChange={(value) =>
                                setField("businessType", value || [])
                              }
                              isMulti
                              error={errors.businessType}
                            />
                            {isOtherSelected(formData.businessType) && (
                              <Field
                                label="Please specify other"
                                name="businessTypeOther"
                                placeholder="Specify other business type"
                                value={formData.businessTypeOther}
                                onChange={handleChange}
                                required={false}
                              />
                            )}
                            <SelectField
                              label="Years in Operation"
                              placeholder="Select one"
                              options={yearsOptions}
                              value={yearsOptions.find(
                                (opt) => opt.value === formData.yearsOperation
                              )}
                              onChange={(value) =>
                                setField("yearsOperation", value.value)
                              }
                              error={errors.yearsOperation}
                            />
                            <Field
                              label="Full Name of Applicant"
                              name="applicantName"
                              placeholder="Enter your name"
                              value={formData.applicantName}
                              onChange={handleChange}
                              icon={User}
                              error={errors.applicantName}
                            />
                            <Field
                              label="Position / Title"
                              name="applicantTitle"
                              placeholder="Position in your organisation"
                              value={formData.applicantTitle}
                              onChange={handleChange}
                              icon={Briefcase}
                              error={errors.applicantTitle}
                            />
                          </>
                        )}

                        {step === 2 && (
                          <>
                            <div>
                              <label className="PF_field_label">
                                Phone Number
                                <span className="PF_field_required">*</span>
                              </label>
                              <div
                                className={`PF_phone ${
                                  errors.phone ? "is-invalid" : ""
                                }`}
                              >
                                <PhoneInput
                                  country={"in"}
                                  value={formData.phone}
                                  onChange={(phone) =>
                                    setField(
                                      "phone",
                                      phone ? `+${phone}` : ""
                                    )
                                  }
                                  dropdownStyle={{ zIndex: 10000 }}
                                />
                              </div>
                              {errors.phone && (
                                <p className="PF_field_error">
                                  {errors.phone}
                                </p>
                              )}
                            </div>
                            <Field
                              label="Email"
                              type="email"
                              name="email"
                              placeholder="you@company.com"
                              value={formData.email}
                              onChange={handleChange}
                              icon={Mail}
                              error={errors.email}
                            />
                            <div>
                              <label className="PF_field_label">
                                Country
                                <span className="PF_field_required">*</span>
                              </label>
                              <div
                                className={`PF_select ${
                                  errors.country ? "is-invalid" : ""
                                }`}
                              >
                                <Select
                                  options={countryOptions}
                                  value={countryOptions.find(
                                    (option) =>
                                      option.value === formData.country
                                  )}
                                  onChange={(selected) =>
                                    setField("country", selected.value)
                                  }
                                  classNamePrefix="react-select"
                                  placeholder="Select your country"
                                />
                              </div>
                              {errors.country && (
                                <p className="PF_field_error">
                                  {errors.country}
                                </p>
                              )}
                            </div>
                            <Field
                              label="City"
                              name="city"
                              placeholder="Enter your city"
                              value={formData.city}
                              onChange={handleChange}
                              icon={Building}
                              error={errors.city}
                            />
                            <Field
                              label="State"
                              name="state"
                              placeholder="Enter your state"
                              value={formData.state}
                              onChange={handleChange}
                              icon={MapPin}
                              required={false}
                            />
                            <TextAreaField
                              label="Business Address"
                              name="address"
                              placeholder="Street, area, postal code"
                              value={formData.address}
                              onChange={handleChange}
                              icon={FileText}
                              error={errors.address}
                            />
                          </>
                        )}

                        {step === 3 && (
                          <>
                            <SelectField
                              label="Who are your existing clients?"
                              placeholder="Choose as many as you like"
                              options={clientsOptions}
                              value={formData.existingClients}
                              onChange={(value) =>
                                setField("existingClients", value || [])
                              }
                              isMulti
                              error={errors.existingClients}
                            />
                            {isOtherSelected(formData.existingClients) && (
                              <Field
                                label="Please specify other clients"
                                name="existingClientsOther"
                                placeholder="Specify other client types"
                                value={formData.existingClientsOther}
                                onChange={handleChange}
                                required={false}
                              />
                            )}
                            <SelectField
                              label="Have you previously sold SaaS, legal, or accounting/taxation software?"
                              placeholder="Select one"
                              options={yesNoOptions}
                              value={yesNoOptions.find(
                                (opt) => opt.value === formData.soldSaaSBefore
                              )}
                              onChange={(value) =>
                                setField("soldSaaSBefore", value.value)
                              }
                              error={errors.soldSaaSBefore}
                            />
                            <SelectField
                              label="Familiarity with SaaS/compliance products"
                              placeholder="Select one"
                              options={productOptions}
                              value={productOptions.find(
                                (opt) => opt.value === formData.familiarity
                              )}
                              onChange={(value) =>
                                setField("familiarity", value.value)
                              }
                              error={errors.familiarity}
                            />
                            <SelectField
                              label="Do you have a dedicated sales/marketing team?"
                              placeholder="Select one"
                              options={yesNoOptions}
                              value={yesNoOptions.find(
                                (opt) => opt.value === formData.hasSalesTeam
                              )}
                              onChange={(value) =>
                                setField("hasSalesTeam", value.value)
                              }
                              error={errors.hasSalesTeam}
                            />
                            <SelectField
                              label="Current marketing activities"
                              placeholder="Choose as many as you like"
                              options={marketingOptions}
                              value={formData.currentMarketing}
                              onChange={(value) =>
                                setField("currentMarketing", value || [])
                              }
                              isMulti
                              error={errors.currentMarketing}
                            />
                            {isOtherSelected(formData.currentMarketing) && (
                              <Field
                                label="Please specify other marketing activities"
                                name="currentMarketingOther"
                                placeholder="Specify other marketing"
                                value={formData.currentMarketingOther}
                                onChange={handleChange}
                                required={false}
                              />
                            )}
                          </>
                        )}
                      </div>

                      <div className="PF_actions">
                        {step > 1 && (
                          <button
                            type="button"
                            onClick={handleBack}
                            className="PF_button PF_button_secondary"
                          >
                            Back
                          </button>
                        )}
                        {step < 3 ? (
                          <button
                            type="button"
                            onClick={handleNext}
                            className="PF_button PF_button_primary"
                          >
                            Next
                          </button>
                        ) : (
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className="PF_button PF_button_primary"
                          >
                            {isSubmitting ? "Submitting…" : "Submit"}
                          </button>
                        )}
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
        <CustomFooter />
      </div>
    </>
  );
};

const Field = ({
  label,
  type = "text",
  name,
  value,
  onChange,
  icon: Icon,
  placeholder,
  error,
  required = true,
}) => (
  <div>
    <label className="PF_field_label">
      {label}
      {required && <span className="PF_field_required">*</span>}
    </label>
    <div className="PF_field_wrap">
      {Icon && (
        <span className="PF_field_icon">
          <Icon size={16} />
        </span>
      )}
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`PF_input ${Icon ? "has-icon" : ""} ${
          error ? "is-invalid" : ""
        }`}
      />
    </div>
    {error && <p className="PF_field_error">{error}</p>}
  </div>
);

const TextAreaField = ({
  label,
  name,
  value,
  onChange,
  icon: Icon,
  placeholder,
  error,
}) => (
  <div>
    <label className="PF_field_label">
      {label}
      <span className="PF_field_required">*</span>
    </label>
    <div className="PF_field_wrap">
      {Icon && (
        <span className="PF_field_icon" style={{ top: 22 }}>
          <Icon size={16} />
        </span>
      )}
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`PF_textarea ${Icon ? "has-icon" : ""} ${
          error ? "is-invalid" : ""
        }`}
        rows={3}
      />
    </div>
    {error && <p className="PF_field_error">{error}</p>}
  </div>
);

const SelectField = ({
  label,
  options,
  value,
  onChange,
  isMulti = false,
  placeholder = "Select",
  error,
}) => (
  <div>
    <label className="PF_field_label">
      {label}
      <span className="PF_field_required">*</span>
    </label>
    <div className={`PF_select ${error ? "is-invalid" : ""}`}>
      <Select
        options={options}
        value={value}
        onChange={onChange}
        isMulti={isMulti}
        classNamePrefix="react-select"
        placeholder={placeholder}
      />
    </div>
    {error && <p className="PF_field_error">{error}</p>}
  </div>
);

export default PartnerForm;
